// ENG-YAKSON-080 (A7): the single gating resolver every FE surface (nav prune,
// route guard, user menu) calls. It answers feature/route visibility using the
// backend's RESOLVED permissions when they're loaded, and transparently falls
// back to the legacy client-side matrix while they're loading or if the fetch
// failed — the no-flash / back-compat spine (Spec A §12).
//
//   ready  → resolved permissions win (FEATURE_TO_KEYS / keysForRoute).
//   !ready → legacy canAccessFeature / canAccessRoute(userType) hold.
//
// Legacy is never deleted (permissions.ts keeps its matrix): it also keeps the
// nav correct for any user before the role seed assigns them a role.
import { useCallback } from 'react';
import { useAuth } from './auth-context';
import { useMyPermissions } from './my-permissions';
import {
  canAccessFeature,
  canAccessRoute,
  type UserType,
} from './permissions';
import {
  FEATURE_TO_KEYS,
  keysForRoute,
  type FeatureName,
  type PermissionKey,
} from './permission-keys';

export interface Gating {
  userType: UserType;
  /** True once resolved permissions are loaded (else legacy fallback is active). */
  ready: boolean;
  /** Feature visible? Resolved keys when ready, legacy matrix otherwise. */
  canFeature: (feature: FeatureName) => boolean;
  /** Route reachable? Resolved keys when ready, legacy matrix otherwise. */
  canRoute: (pathnameWithoutLocale: string) => boolean;
  /** Holds this specific key (non-NONE)? Resolved keys when ready; legacy
   *  fallback treats a key as admin-only (matches the pre-RBAC require_admin),
   *  so a granted key only lights up once resolved perms load (no flash-grant). */
  canKey: (key: PermissionKey) => boolean;
}

export function useGating(): Gating {
  const { user } = useAuth();
  const { hasPermission, ready } = useMyPermissions();
  const userType = (user?.userType || 'RECEPTIONIST') as UserType;

  const canFeature = useCallback(
    (feature: FeatureName): boolean => {
      if (!ready) return canAccessFeature(userType, feature);
      return hasPermission(FEATURE_TO_KEYS[feature]);
    },
    [ready, hasPermission, userType],
  );

  const canRoute = useCallback(
    (pathnameWithoutLocale: string): boolean => {
      if (!ready) return canAccessRoute(userType, pathnameWithoutLocale);
      const keys = keysForRoute(pathnameWithoutLocale);
      if (keys === null) return true; // unmapped route — allow (404s on its own)
      return hasPermission(keys);
    },
    [ready, hasPermission, userType],
  );

  const canKey = useCallback(
    (key: PermissionKey): boolean => {
      if (!ready) return userType === 'ADMIN';
      return hasPermission([key]);
    },
    [ready, hasPermission, userType],
  );

  return { userType, ready, canFeature, canRoute, canKey };
}
